
import { EmailWithType } from '@/types/lead';

/**
 * Formats an email value for display, handling different types:
 * - string: returns the string directly
 * - EmailWithType: returns the email property
 * - EmailWithType[]: returns a comma-separated list of email addresses
 */
export const formatEmailForDisplay = (email: string | EmailWithType | EmailWithType[] | null | undefined): string => {
  if (!email) return '';
  
  if (typeof email === 'string') {
    return email;
  }
  
  if (Array.isArray(email)) {
    return email.map(e => e.email).join(', ');
  }
  
  // Single EmailWithType object
  return email.email;
};

/**
 * Returns the primary or first email address from any email format
 */
/**
 * Extracts a website URL from an email domain (e.g. sales@officepro.ie → https://officepro.ie)
 * Returns null if no valid domain can be derived (generic providers are excluded).
 */
export const extractWebsiteFromEmail = (email: string | EmailWithType | EmailWithType[] | null | undefined): string | null => {
  if (!email) return null;

  const genericProviders = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'live.com', 'msn.com',
    'ymail.com', 'googlemail.com', 'me.com', 'mac.com',
  ];

  const tryExtract = (addr: string): string | null => {
    if (!addr || !addr.includes('@')) return null;
    const domain = addr.split('@')[1]?.toLowerCase();
    if (domain && !genericProviders.includes(domain)) {
      return `https://${domain}`;
    }
    return null;
  };

  if (typeof email === 'string') return tryExtract(email);

  if (Array.isArray(email)) {
    for (const e of email) {
      const result = tryExtract(e.email);
      if (result) return result;
    }
    return null;
  }

  return tryExtract(email.email);
};

/**
 * Returns the primary or first email address from any email format
 */
export const getPrimaryEmail = (email: string | EmailWithType | EmailWithType[] | null | undefined): string => {
  if (!email) return '';
  
  if (typeof email === 'string') {
    return email;
  }
  
  if (Array.isArray(email)) {
    // Try to find a primary email (e.g. info@, contact@)
    const primaryTypes = ['primary', 'main', 'info', 'contact', 'general'];
    
    // First look for emails with primary types
    for (const type of primaryTypes) {
      const primaryEmail = email.find(e => 
        e.type && e.type.toLowerCase().includes(type)
      );
      if (primaryEmail) return primaryEmail.email;
    }
    
    // If no primary type is found, return the first email
    return email.length > 0 ? email[0].email : '';
  }
  
  // Single EmailWithType object
  return email.email;
};
